/**
 * Carousel progress bar.
 *
 * Drives .products-slider__progress-fill from how far the carousel has been
 * scrolled. Two sources, because products-slider renders two implementations
 * and swaps them at 1200px:
 *
 *   >= 1200px  Swiper  -> the `progress` event (fires continuously during a
 *                         drag, so the bar tracks the finger)
 *   <  1200px  native  -> scrollLeft on the overflow scroller
 *
 * Timing: global.js builds the Swipers on DOMContentLoaded, and on a page with
 * several carousels that lands well after this script runs. Rather than poll
 * on a time budget (which expired on slower loads), we watch the .swiper
 * element for the `swiper-initialized` class Swiper adds when it boots.
 * global.js also destroys and recreates Swiper when the viewport crosses
 * 1200px, so everything re-binds on resize and on shopify:section:load.
 */
(function () {
  // The section template emits this script once per section instance, so the
  // file executes several times on a page with several carousels.
  if (window.__tvfSliderProgressInit) return;
  window.__tvfSliderProgressInit = true;

  function clamp01(value) {
    if (!isFinite(value) || value < 0) return 0;
    return value > 1 ? 1 : value;
  }

  function ProgressBar(root) {
    this.root = root;
    this.el = root.querySelector(".products-slider__progress");
    this.fill = root.querySelector(".products-slider__progress-fill");
    this.cleanups = [];
  }

  ProgressBar.prototype.set = function (value) {
    if (!this.fill) return;
    this.current = clamp01(value);
    this.fill.style.transform = "scaleX(" + this.current.toFixed(4) + ")";
  };

  /**
   * Eases to a value over `duration`. Used for arrow clicks and the snap-back,
   * where Swiper reports the new progress in one jump rather than per frame.
   * Done here rather than with a css transition — see component-slider-progress.css.
   */
  ProgressBar.prototype.animateTo = function (value, duration) {
    var self = this;
    var target = clamp01(value);

    if (this.raf) {
      cancelAnimationFrame(this.raf);
      this.raf = null;
    }

    var from = typeof this.current === "number" ? this.current : 0;
    var delta = target - from;

    if (!duration || Math.abs(delta) < 0.001 || this.reducedMotion()) {
      this.set(target);
      return;
    }

    var start = null;
    var step = function (now) {
      if (start === null) start = now;
      var t = Math.min(1, (now - start) / duration);
      // easeOutCubic - quick off the mark, settles gently.
      var eased = 1 - Math.pow(1 - t, 3);
      self.set(from + delta * eased);
      self.raf = t < 1 ? requestAnimationFrame(step) : null;
    };
    this.raf = requestAnimationFrame(step);
  };

  ProgressBar.prototype.reducedMotion = function () {
    return (
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    );
  };

  // Hidden rather than shown permanently empty when nothing can scroll.
  ProgressBar.prototype.setInert = function (inert) {
    if (this.el) this.el.classList.toggle("is-inert", !!inert);
  };

  // While a finger or mouse is down the bar follows it exactly, one write per
  // progress event and no easing, so it never lags behind the pointer.
  ProgressBar.prototype.setDragging = function (dragging) {
    this.dragging = !!dragging;
    if (dragging && this.raf) {
      cancelAnimationFrame(this.raf);
      this.raf = null;
    }
  };

  ProgressBar.prototype.teardown = function () {
    if (this.raf) {
      cancelAnimationFrame(this.raf);
      this.raf = null;
    }
    this.cleanups.forEach(function (fn) {
      try { fn(); } catch (e) {}
    });
    this.cleanups = [];
  };

  ProgressBar.prototype.bindSwiper = function (swiper) {
    var self = this;

    var onProgress = function (sw, progress) {
      if (self.dragging) {
        self.set(progress);
      } else {
        self.animateTo(progress, 260);
      }
    };
    var onTouchStart = function () { self.setDragging(true); };
    var onTouchEnd = function () { self.setDragging(false); };
    var sync = function () {
      self.setInert(swiper.isLocked);
      self.set(swiper.progress);
    };

    swiper.on("progress", onProgress);
    swiper.on("touchStart", onTouchStart);
    swiper.on("touchEnd", onTouchEnd);
    swiper.on("resize", sync);
    swiper.on("update", sync);
    sync();

    this.cleanups.push(function () {
      if (swiper.destroyed) return;
      swiper.off("progress", onProgress);
      swiper.off("touchStart", onTouchStart);
      swiper.off("touchEnd", onTouchEnd);
      swiper.off("resize", sync);
      swiper.off("update", sync);
    });
  };

  ProgressBar.prototype.bindNative = function (scroller) {
    var self = this;
    var frame = null;

    var update = function () {
      var max = scroller.scrollWidth - scroller.clientWidth;
      self.setInert(max <= 1);
      self.set(max > 0 ? scroller.scrollLeft / max : 0);
    };

    var onScroll = function () {
      if (frame) return;
      frame = requestAnimationFrame(function () {
        frame = null;
        update();
      });
    };

    scroller.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", update);
    update();

    this.cleanups.push(function () {
      scroller.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", update);
      if (frame) cancelAnimationFrame(frame);
    });
  };

  /**
   * Both DOM trees exist at all times; CSS decides which is visible, so
   * offsetParent is the test for which one to follow.
   */
  ProgressBar.prototype.attach = function () {
    this.teardown();

    var nativeScroller = this.root.querySelector(
      ".products-slider__native-scroller"
    );
    if (nativeScroller && nativeScroller.offsetParent !== null) {
      this.bindNative(nativeScroller);
      return true;
    }

    var swiperEl = this.root.querySelector(".swiper");
    if (swiperEl && swiperEl.swiper && !swiperEl.swiper.destroyed) {
      this.bindSwiper(swiperEl.swiper);
      return true;
    }

    return false;
  };

  /**
   * Waits for Swiper to boot rather than racing it. `swiper-initialized` is
   * added by Swiper itself on creation, so the observer fires exactly once,
   * whenever that happens, with no time limit to run out.
   */
  ProgressBar.prototype.attachWhenReady = function () {
    var self = this;
    if (this.attach()) return;

    var swiperEl = this.root.querySelector(".swiper");
    if (!swiperEl || typeof MutationObserver === "undefined") return;

    var observer = new MutationObserver(function () {
      if (!swiperEl.classList.contains("swiper-initialized")) return;
      if (self.attach()) {
        observer.disconnect();
        self.cleanups.push(function () { observer.disconnect(); });
      }
    });
    observer.observe(swiperEl, {
      attributes: true,
      attributeFilter: ["class"],
    });

    this.observer = observer;
  };

  var bars = [];

  function init() {
    bars.forEach(function (bar) {
      if (bar.observer) bar.observer.disconnect();
      bar.teardown();
    });
    bars = [];

    Array.prototype.forEach.call(
      document.querySelectorAll(".products-slider"),
      function (root) {
        if (!root.querySelector(".products-slider__progress")) return;
        var bar = new ProgressBar(root);
        bars.push(bar);
        bar.attachWhenReady();
      }
    );
  }

  function ready(fn) {
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", fn);
    } else {
      fn();
    }
  }

  ready(function () {
    init();

    document.addEventListener("shopify:section:load", init);

    // global.js rebuilds Swiper 150ms after a resize settles; re-bind after it.
    var resizeTimer;
    window.addEventListener("resize", function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(init, 250);
    });
  });
})();
